export interface TimeSlot {
  id?: string;
  start: string;
  end: string;
  availableDays?: number[]; // Array vide = tous les jours, [0-6] où 0=dimanche, 6=samedi
  durationOverrideMinutes?: number | null; // Surcharge de la durée de réservation pour ce créneau
}

export interface TimeSlotDateOverride {
  id: string;
  establishmentId: string;
  date: string; // 'YYYY-MM-DD'
  startTime: string; // 'HH:mm'
  endTime: string; // 'HH:mm'
  durationOverrideMinutes?: number | null;
  label?: string;
  isActive: boolean;
}

/**
 * Retourne les créneaux effectifs pour une date donnée.
 * Si des overrides existent pour cette date, ils remplacent entièrement les créneaux réguliers.
 * Sinon, filtre les créneaux réguliers par jour de la semaine (available_days).
 */
export function getTimeSlotsForDate(
  date: Date,
  regularSlots: TimeSlot[],
  dateOverrides: TimeSlotDateOverride[]
): TimeSlot[] {
  const dateStr = formatDateForOverride(date);
  
  // Chercher les overrides actifs pour cette date
  const overridesForDate = dateOverrides.filter(
    o => o.date === dateStr && o.isActive
  );
  
  if (overridesForDate.length > 0) {
    // Convertir les overrides en TimeSlot[]
    return overridesForDate.map(o => ({
      id: o.id,
      start: o.startTime,
      end: o.endTime,
      durationOverrideMinutes: o.durationOverrideMinutes ?? null,
    }));
  }
  
  // Pas d'override → filtrer par jour de la semaine
  const dayOfWeek = date.getDay();
  return regularSlots.filter(ts =>
    !ts.availableDays || ts.availableDays.length === 0 || ts.availableDays.includes(dayOfWeek)
  );
}

/**
 * Jours de la semaine (0=dimanche … 6=samedi) où aucun créneau régulier n'est actif.
 * Un créneau avec availableDays vide couvre tous les jours.
 */
export function getWeeklyClosedDays(regularSlots: TimeSlot[]): number[] {
  if (regularSlots.length === 0) {
    return [0, 1, 2, 3, 4, 5, 6];
  }

  const activeDays = new Set<number>();
  for (const ts of regularSlots) {
    if (!ts.availableDays || ts.availableDays.length === 0) {
      return [];
    }
    for (const day of ts.availableDays) {
      activeDays.add(day);
    }
  }

  return [0, 1, 2, 3, 4, 5, 6].filter(day => !activeDays.has(day));
}

/**
 * True si la date tombe un jour hebdomadaire sans service
 * (sauf s'il existe des créneaux override actifs pour cette date).
 */
export function isWeeklyClosedDate(
  date: Date,
  regularSlots: TimeSlot[],
  dateOverrides: TimeSlotDateOverride[] = []
): boolean {
  return getTimeSlotsForDate(date, regularSlots, dateOverrides).length === 0;
}

/**
 * Vérifie si une date a des overrides (créneaux spécifiques).
 */
export function hasDateOverrides(date: Date, dateOverrides: TimeSlotDateOverride[]): boolean {
  const dateStr = formatDateForOverride(date);
  return dateOverrides.some(o => o.date === dateStr && o.isActive);
}

function formatDateForOverride(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Formate une heure de début en label complet avec l'heure de fin
 * @param startTime - Heure de début au format "HH:mm"
 * @param timeSlots - Liste des créneaux horaires disponibles
 * @returns Label formaté "HH:mm - HH:mm" ou juste startTime si pas trouvé
 */
export function getTimeSlotLabel(startTime: string, timeSlots: TimeSlot[]): string {
  const slot = timeSlots.find(s => s.start === startTime);
  return slot ? `${slot.start} - ${slot.end}` : startTime;
}

/**
 * Extrait l'heure de début d'un timeSlot au format "HH:mm - HH:mm"
 * @param timeSlot - TimeSlot au format "HH:mm - HH:mm" ou "HH:mm"
 * @returns Heure de début au format "HH:mm"
 */
export function parseTimeSlotStart(timeSlot: string): string {
  return timeSlot.includes(' - ') ? timeSlot.split(' - ')[0] : timeSlot;
}

/**
 * Vérifie si une date/heure donnée a un créneau valide
 * @param date - Date à vérifier
 * @param startTime - Heure de début à vérifier
 * @param timeSlots - Liste des créneaux horaires disponibles
 * @returns true si le créneau existe
 */
export function isValidTimeSlot(date: Date, startTime: string, timeSlots: TimeSlot[]): boolean {
  return timeSlots.some(slot => slot.start === startTime);
}

/**
 * Trouve l'ID du créneau parent pour une heure donnée.
 * En mode SLOT : match direct par heure de début ("11:30 - 14:00" → slot commençant à 11:30).
 * En mode TIME : trouve le créneau qui contient cette heure ("12:30" → slot 11:30-14:00).
 */
export function findParentSlotId(
  time: string,
  timeSlots: { id?: string; start: string; end: string }[]
): string | undefined {
  const startTime = time.includes(' - ') ? time.split(' - ')[0].trim() : time.trim();
  const normalized = startTime.substring(0, 5);

  // Direct match (SLOT mode)
  const directMatch = timeSlots.find(ts => ts.start.substring(0, 5) === normalized);
  if (directMatch?.id) return directMatch.id;

  // Containing slot (TIME mode)
  const [h, m] = normalized.split(':').map(Number);
  const timeMin = h * 60 + m;

  const containingSlot = timeSlots.find(ts => {
    const [sh, sm] = ts.start.substring(0, 5).split(':').map(Number);
    const [eh, em] = ts.end.substring(0, 5).split(':').map(Number);
    const s = sh * 60 + sm, e = eh * 60 + em;
    // Créneau de nuit (fin < début) : couvre aussi la portion après minuit
    if (e < s) return timeMin >= s || timeMin < e;
    return timeMin >= s && timeMin < e;
  });
  return containingSlot?.id;
}

/**
 * Résout la durée effective de réservation pour un horaire donné.
 * Si le créneau parent a un `durationOverrideMinutes`, l'utilise ;
 * sinon, retourne la durée globale.
 */
export function getEffectiveDuration(
  time: string,
  timeSlots: TimeSlot[],
  globalDuration: number
): number {
  const startTime = time.includes(' - ') ? time.split(' - ')[0].trim() : time.trim();
  const normalized = startTime.substring(0, 5);
  const [h, m] = normalized.split(':').map(Number);
  const timeMin = h * 60 + m;

  // Find containing slot (TIME mode) or direct match (SLOT mode)
  const slot = timeSlots.find(ts => {
    const start = ts.start.substring(0, 5);
    const end = ts.end.substring(0, 5);
    // Direct match
    if (start === normalized) return true;
    // Containing match
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const s = sh * 60 + sm, e = eh * 60 + em;
    if (e < s) return timeMin >= s || timeMin < e;
    return timeMin >= s && timeMin < e;
  });

  return slot?.durationOverrideMinutes ?? globalDuration;
}
